import { supabase } from './supabase';
import { formatDate, formatPrizePool } from './tournaments';

/**
 * Tournament history on player profiles.
 *
 * Backed by the SECURITY DEFINER RPC `get_user_tournament_history(p_user_id)`
 * (supabase/migrations/20261026040000_user_tournament_history_rpc.sql): one
 * row per finished public event the player took part in, newest first, with
 * their own line from the Overstat results snapshot when the event has one.
 *
 * Fails soft: any error (including the RPC not being deployed yet) resolves
 * to `null`, and callers hide the section.
 */

export interface TournamentHistoryEntry {
  tournamentId: string;
  slug: string;
  title: string;
  gameName: string;
  startsAt: string;
  teamSize: number;
  format: string | null;
  prizePoolCents: number;
  currency: string;
  coverImage: string | null;
  /** Players in the results snapshot, else the registration count. */
  fieldSize: number | null;
  /** True when per-player stats exist for this event. */
  hasResults: boolean;
  /** Overstat player rank (ranks players, not teams). */
  rank: number | null;
  kills: number | null;
  damage: number | null;
  avgPlacement: number | null;
  gamesPlayed: number | null;
  team: string | null;
}

export interface TournamentHistorySummary {
  played: number;
  bestRank: number | null;
  /** The event the best rank came from (most recent on ties). */
  best: TournamentHistoryEntry | null;
  topTens: number;
  kills: number;
}

export interface TournamentHistoryRow {
  tournament_id: string;
  slug: string;
  title: string;
  game_name: string;
  starts_at: string;
  team_size: number | null;
  format: string | null;
  prize_pool_cents: number | null;
  currency: string | null;
  cover_image: string | null;
  field_size: number | null;
  has_results: boolean;
  rank: number | null;
  kills: number | null;
  damage: number | null;
  avg_placement: number | string | null;
  games_played: number | null;
  team: string | null;
}

const num = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
};

export function mapHistoryRow(r: TournamentHistoryRow): TournamentHistoryEntry {
  return {
    tournamentId: r.tournament_id,
    slug: r.slug,
    title: r.title,
    gameName: r.game_name,
    startsAt: r.starts_at,
    teamSize: r.team_size ?? 1,
    format: r.format,
    prizePoolCents: r.prize_pool_cents ?? 0,
    currency: r.currency ?? 'USD',
    coverImage: r.cover_image,
    fieldSize: num(r.field_size),
    hasResults: !!r.has_results,
    rank: num(r.rank),
    kills: num(r.kills),
    damage: num(r.damage),
    avgPlacement: num(r.avg_placement),
    gamesPlayed: num(r.games_played),
    team: r.team,
  };
}

/** The player's history, or null when it can't be loaded (hide the section). */
export async function getUserTournamentHistory(
  userId: string
): Promise<TournamentHistoryEntry[] | null> {
  if (!userId) return null;
  try {
    const { data, error } = await supabase.rpc('get_user_tournament_history', {
      p_user_id: userId,
    });
    if (error) {
      // PGRST202 = function not deployed yet; expected until the migration lands.
      if (error.code !== 'PGRST202') {
        console.warn('getUserTournamentHistory error:', error.message);
      }
      return null;
    }
    return ((data ?? []) as TournamentHistoryRow[]).map(mapHistoryRow);
  } catch {
    return null;
  }
}

export function summarizeHistory(entries: TournamentHistoryEntry[]): TournamentHistorySummary {
  let best: TournamentHistoryEntry | null = null;
  let topTens = 0;
  let kills = 0;
  for (const e of entries) {
    if (e.rank !== null) {
      if (!best || (best.rank !== null && e.rank < best.rank)) best = e;
      if (e.rank <= 10) topTens += 1;
    }
    kills += e.kills ?? 0;
  }
  return { played: entries.length, bestRank: best?.rank ?? null, best, topTens, kills };
}

/* ------------------------------------------------------------------ */
/* Display helpers                                                     */
/* ------------------------------------------------------------------ */

export const PLACE_COLORS = {
  gold: '#F5C451',
  silver: '#C9D1D9',
  bronze: '#D08A57',
} as const;

/** Pill / medal colors for a finish. Unranked ("Played") is muted. */
export function placeStyle(rank: number | null): { color: string; border: string; tint: string } {
  if (rank === 1) return { color: PLACE_COLORS.gold, border: 'rgba(245,196,81,0.5)', tint: 'rgba(245,196,81,0.12)' };
  if (rank === 2) return { color: PLACE_COLORS.silver, border: 'rgba(201,209,217,0.5)', tint: 'rgba(201,209,217,0.12)' };
  if (rank === 3) return { color: PLACE_COLORS.bronze, border: 'rgba(208,138,87,0.55)', tint: 'rgba(208,138,87,0.12)' };
  if (rank) return { color: '#ffffff', border: 'rgba(255,255,255,0.2)', tint: 'rgba(255,255,255,0.06)' };
  return { color: 'rgba(255,255,255,0.6)', border: 'rgba(255,255,255,0.12)', tint: 'rgba(255,255,255,0.04)' };
}

/** "Apex" for Apex Legends; other games keep their name. */
export function shortGameName(name: string): string {
  return name === 'Apex Legends' ? 'Apex' : name;
}

export function teamModeLabel(teamSize: number): string {
  switch (teamSize) {
    case 1:
      return 'Solo';
    case 2:
      return 'Duos';
    case 3:
      return 'Trios';
    case 4:
      return 'Squads';
    default:
      return `${teamSize}v${teamSize}`;
  }
}

/** "Jun 27 · Apex · Duos" plus optional extras. */
export function entryMeta(
  e: TournamentHistoryEntry,
  opts: { field?: boolean; prize?: boolean } = {}
): string {
  const parts = [
    formatDate(e.startsAt, undefined, 'UTC'),
    shortGameName(e.gameName),
    teamModeLabel(e.teamSize),
  ];
  if (opts.field && e.rank && e.fieldSize) parts.push(`of ${e.fieldSize}`);
  if (opts.prize && e.prizePoolCents > 0) {
    parts.push(`${formatPrizePool(e.prizePoolCents, e.currency)} pool`);
  }
  return parts.join(' · ');
}

/** "top 6%" for a ranked finish, when the field size is known. */
export function topPercent(rank: number | null, field: number | null): string | null {
  if (!rank || !field || field <= 0) return null;
  return `top ${Math.max(1, Math.ceil((rank / field) * 100))}%`;
}
